import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AlertCircle, Info, MessageSquareText, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DiscordAutomatedMessagesDialog,
  type DiscordAutomatedMessageRuleKey,
  type DiscordAutomatedMessageSaveSource,
} from './DiscordAutomatedMessages.dialog';
import type {
  DiscordAdvancedGuildList,
  DiscordAutomatedMessageOptions,
  DiscordAutomatedMessages,
  DiscordGuildSummary,
} from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import type { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Empty, EmptyDescription, EmptyHeader } from '@/Domain/components/Shared/Empty/Empty';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const DISCORD_INTENTS_DOCS =
  'https://discord.com/developers/docs/events/gateway#privileged-intents';
const DISCORD_INTEGRATION_QUERY_KEY = [QueryKey.integration.get, 'discord'] as const;

interface DiscordAdvancedAutomatedMessagesProps {
  open: boolean;
  enabled: boolean;
  blocked: boolean;
  hideEmptyState?: boolean;
}

export const DiscordAdvancedAutomatedMessages = ({
  open,
  enabled,
  blocked,
  hideEmptyState = false,
}: DiscordAdvancedAutomatedMessagesProps) => {
  const { t } = useTranslation(['integration']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [openGuildId, setOpenGuildId] = useState<string | null>(null);

  useEffect(() => {
    if (blocked) setOpenGuildId(null);
  }, [blocked]);

  const {
    data: serverList,
    isLoading,
    isError,
    error: listError,
    refetch,
  } = useQuery<DiscordAdvancedGuildList, AxiosError<ErrorResponse>>({
    queryKey: [QueryKey.integration.discordAdvancedGuilds],
    enabled: open && enabled,
  });

  const guilds = serverList?.guilds;
  const guild = guilds?.find((row) => row.guild_id === openGuildId) ?? null;
  const dialogGuild = useMemo<DiscordGuildSummary | null>(
    () =>
      guild
        ? {
            guild_id: guild.guild_id,
            guild_name: guild.guild_name,
            automated_messages: guild.automated_messages,
            enabled: true,
            state: 'ACTIVE',
            added_at: null,
          }
        : null,
    [guild],
  );

  const {
    data: options,
    isLoading: optionsLoading,
    isError: optionsError,
    refetch: refetchOptions,
  } = useQuery<DiscordAutomatedMessageOptions>({
    queryKey: [QueryKey.integration.discordAdvancedAutomatedMessageOptions, openGuildId ?? ''],
    enabled: open && enabled && openGuildId !== null,
  });

  const invalidateDiscordStatus = () => {
    void queryClient.invalidateQueries({ queryKey: DISCORD_INTEGRATION_QUERY_KEY });
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.integration.discordTokenStatus],
    });
  };

  const saveMutation = useMutation<
    DiscordAutomatedMessages,
    AxiosError<ErrorResponse>,
    {
      guildId: string;
      automatedMessages: DiscordAutomatedMessages;
      rule: DiscordAutomatedMessageRuleKey;
      source: DiscordAutomatedMessageSaveSource;
    }
  >({
    mutationKey: [QueryKey.integration.discordAdvancedAutomatedMessagesUpdate],
    onSuccess: (automatedMessages, variables) => {
      queryClient.setQueryData<DiscordAdvancedGuildList>(
        [QueryKey.integration.discordAdvancedGuilds],
        (previous) =>
          previous && {
            ...previous,
            guilds: previous.guilds.map((row) =>
              row.guild_id === variables.guildId
                ? { ...row, automated_messages: automatedMessages }
                : row,
            ),
          },
      );
      if (variables.rule === 'member_joined') {
        void queryClient.invalidateQueries({
          queryKey: [QueryKey.integration.discordAdvancedGuilds],
        });
        invalidateDiscordStatus();
      }
      capture(PostHogEventEnum.DiscordIntegrationAutomatedMessagesSaved, {
        channel_created_enabled: automatedMessages.channel_created.enabled,
        member_joined_enabled: automatedMessages.member_joined.enabled,
        thread_created_enabled: automatedMessages.thread_created.enabled,
        saved_rule: variables.rule,
        source: variables.source,
        method: 'advanced',
      });
      toast.success(t('integration:discord.oneclick.automated_messages.saved'));
    },
    onError: (error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.integration.discordAdvancedGuilds],
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.integration.discordAdvancedAutomatedMessageOptions],
      });
      if (variables.rule === 'member_joined') invalidateDiscordStatus();
      const reason = error.response?.data?.errors?.root?.[0]?.message;
      toast.error(reason || t('integration:discord.oneclick.automated_messages.save_failed'));
    },
  });

  const handleSave = async (
    guildId: string,
    automatedMessages: DiscordAutomatedMessages,
    meta: { rule: DiscordAutomatedMessageRuleKey; source: DiscordAutomatedMessageSaveSource },
  ): Promise<boolean> => {
    try {
      await saveMutation.mutateAsync({
        guildId,
        automatedMessages,
        ...meta,
      });
      return true;
    } catch {
      return false;
    }
  };

  const membersIntentNotice = (rule: DiscordAutomatedMessageRuleKey) => {
    const intent = serverList?.server_members_intent ?? 'unknown';
    if (rule !== 'member_joined' || intent === 'on') return null;
    const missing = intent === 'off';
    return (
      <Alert
        variant={missing ? 'warning' : 'info'}
        data-testid="discord-advanced-members-intent-notice"
        data-intent={intent}
      >
        {missing ? <TriangleAlert data-icon="inline-start" /> : <Info data-icon="inline-start" />}
        <AlertDescription className="flex flex-col items-start gap-2">
          <p className="m-0">
            {t(
              missing
                ? 'integration:discord.advanced_automated_messages.members_intent_off'
                : 'integration:discord.advanced_automated_messages.members_intent',
            )}
          </p>
          <a
            className="text-sm underline underline-offset-4"
            href={DISCORD_INTENTS_DOCS}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('integration:discord.advanced_automated_messages.members_intent_link')}
          </a>
        </AlertDescription>
      </Alert>
    );
  };

  if (!enabled || (hideEmptyState && !isLoading && !isError && !guilds?.length)) return null;

  return (
    <IntegrationSection
      title={t('integration:discord.oneclick.automated_messages.open')}
      description={t('integration:discord.advanced_automated_messages.description')}
      data-testid="discord-advanced-automated-messages-section"
    >
      {isLoading ? (
        <div className="flex justify-center py-6" data-testid="discord-advanced-guilds-loading">
          <Spinner />
        </div>
      ) : isError ? (
        <Alert variant="destructive" data-testid="discord-advanced-guilds-error">
          <AlertCircle data-icon="inline-start" />
          <AlertDescription className="flex flex-col items-start gap-3">
            <p className="m-0">
              {listError?.response?.data?.errors?.root?.[0]?.message ||
                t('integration:discord.advanced_automated_messages.load_failed')}
            </p>
            <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
              {t('integration:discord.oneclick.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : guilds?.length ? (
        <ItemGroup className="gap-2">
          {guilds.map((row) => (
            <Item
              key={row.guild_id}
              variant="outline"
              size="sm"
              className="cursor-default gap-3"
              data-testid={`discord-advanced-guild-${row.guild_id}`}
            >
              <ItemContent className="min-w-0 gap-0.5">
                <ItemTitle>{row.guild_name}</ItemTitle>
                <ItemDescription className="line-clamp-1 text-xs">
                  {t('integration:discord.advanced_automated_messages.rules_on', {
                    count: [
                      row.automated_messages?.member_joined?.enabled,
                      row.automated_messages?.thread_created?.enabled,
                      row.automated_messages?.channel_created?.enabled,
                    ].filter(Boolean).length,
                  })}
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    capture(PostHogEventEnum.DiscordIntegrationAutomatedMessagesOpened, {
                      method: 'advanced',
                    });
                    setOpenGuildId(row.guild_id);
                  }}
                  disabled={blocked}
                  data-testid={`discord-advanced-automated-messages-${row.guild_id}`}
                >
                  <MessageSquareText data-icon="inline-start" />
                  {t('integration:discord.advanced_automated_messages.configure')}
                </Button>
              </ItemActions>
            </Item>
          ))}
        </ItemGroup>
      ) : (
        <Empty className="py-6" data-testid="discord-advanced-no-guilds">
          <EmptyHeader>
            <EmptyDescription>
              {t('integration:discord.advanced_automated_messages.no_servers')}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      <DiscordAutomatedMessagesDialog
        open={open && guild !== null && !blocked}
        guild={dialogGuild}
        options={options}
        optionsLoading={optionsLoading}
        optionsError={optionsError}
        saving={saveMutation.isPending}
        onOpenChange={(dialogOpen) => {
          if (!dialogOpen) setOpenGuildId(null);
        }}
        onRetryOptions={() => void refetchOptions()}
        noticeForRule={membersIntentNotice}
        onSave={handleSave}
      />
    </IntegrationSection>
  );
};

export default DiscordAdvancedAutomatedMessages;
